import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
} from '@angular/common/http';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, mergeMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { LogoutErrorComponent } from 'src/app/main/logout-error/logout-error.component';
import { AuthenticationService } from '@_services/authentication.service';
import { isPublicUnauthenticatedRequest, isSessionGoneApiBody } from './session-http.util';
import { DIALOG_WIDTH } from '../_constant/dialog-width.constants';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  dialogRef: any;
  constructor(
    private authenticationService: AuthenticationService,
    public toasterService: ToastrService,
    private router: Router,
    public dialog: MatDialog,
  ) {}

  /**
   * Displays an error toast only when the same message is not already
   * displayed by the toaster service.
   *
   * This prevents duplicate error notifications when multiple HTTP
   * requests return the same business error at approximately the same time.
   *
   * @param message - The error message to display. If the message is empty,
   *   no toast is displayed.
   * @returns `void`.
   */
  private showErrorToastOnce(message: string): void {
    if (!message) {
      return;
    }
    if (this.toasterService.toasts.some((toast) => toast.message === message)) {
      return;
    }
    this.toasterService.error(message);
  }

  /**
   * Intercepts outgoing HTTP requests and processes their responses for
   * application-level business errors and session-related conditions.
   *
   *
   * Session errors from authenticated APIs are swallowed after triggering
   * the forced logout flow. Public unauthenticated requests are allowed
   * to continue so that login, SSO, and signup flows can process their
   * response bodies normally.
   *
   * @param request - The HTTP request being intercepted.
   * @param next - The next HTTP handler in the interceptor chain.
   * @returns An observable containing the HTTP event stream. Depending on
   *   the response and application state, the stream may emit the original
   *   event, complete without emitting an event, or terminate with an error.
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      mergeMap((evt) => {
        if (!(evt instanceof HttpResponse) || !evt.body) {
          return of(evt);
        }
        if (isSessionGoneApiBody(evt.body)) {
          if (isPublicUnauthenticatedRequest(request)) {
            if (!this.authenticationService.isLoggingOut) {
              this.handleBusinessResponse(evt);
            }
            return of(evt);
          }
          if (!this.authenticationService.isLoggingOut && localStorage.getItem('session_token')) {
            this.authenticationService.ForcLogout();
          }
          return EMPTY;
        }
        if (this.authenticationService.isLoggingOut) {
          return of(evt);
        }
        this.handleBusinessResponse(evt);
        return of(evt);
      }),
      catchError((err) => {
        if (this.authenticationService.isLoggingOut) {
          return EMPTY;
        }
        if (err.status === 400) {
          this.authenticationService.logout();
          this.router.navigate(['login']);
        }
        return throwError(
          'Please hang on a moment, we are working on this and your session will resume in a few minutes. If it does not resume within 5 minutes, please contact SILQ on 1300 55 66 89.',
        );
      }),
    );
  }

  /**
   * Processes application-level business responses returned by the API.
   *
   * The API may return business errors inside an HTTP response body even
   * when the underlying HTTP request itself was successful. This method
   * evaluates the API response code and status and performs the appropriate
   * UI or authentication action.
   *
   * Certain API error codes are intentionally excluded from the generic
   * error-toast handling because they have their own application-specific
   * handling.
   *
   * @param evt - The HTTP response containing the API business response
   *   in its body.
   * @returns `void`.
   */
  private handleBusinessResponse(evt: HttpResponse<any>): void {
    const body = evt.body;
    if (
      body.CODE == 200 &&
      (body.STATUS == 'success' || body.RESPONSE == 'success' || body.STATUS == 'OK')
    ) {
      return;
    } else if (body.CODE == 200 && body.STATUS != 'success') {
      this.showErrorToastOnce(body.STATUS);
    } else if (body.CODE == 406 && body.MESSAGE == 'Permission denied') {
      return;
    } else if (
      body.CODE >= 400 &&
      body.CODE < 499 &&
      (body.STATUS == 'error' || body.RESPONSE == 'error')
    ) {
      if (
        body.MESSAGE &&
        body.MESSAGE != '' &&
        body.STATUS != 'success' &&
        body.CODE != 450 &&
        body.CODE != 444 &&
        body.CODE != '450' &&
        body.CODE != 451 &&
        body.CODE != 402 &&
        body.CODE != 490 &&
        body.CODE != 403 &&
        body.CODE != 422 &&
        body.CODE != '422'
      ) {
        this.showErrorToastOnce(body.MESSAGE);
      }
      if (body.CODE == 490 || body.CODE == '490' || body.CODE == 403) {
        const url = this.router.url;
        window.active = true;
        this.authenticationService.clearLocal();
        localStorage.setItem('logoutmessage', JSON.stringify(body));
        if (!this.dialogRef && !this.router.url.includes('/login?')) {
          this.dialogRef = this.dialog.open(LogoutErrorComponent, {
            disableClose: true,
            panelClass: 'dialog-panel-class',
            backdropClass: 'cdk-overlay-dark-backdrop',
            width: DIALOG_WIDTH.W400,
            data: body,
          });
          this.dialogRef.afterClosed().subscribe((result) => {
            if (result === 0 || result === false) {
              const currentUser = JSON.parse(localStorage.getItem('currentUser'));
              const token = localStorage.getItem('session_token');
              if (currentUser && token) {
                this.dialogRef = null;
                window.location.reload();
              } else {
                this.router.navigate(['login'], {
                  queryParams: {
                    returnUrl: url,
                  },
                });
              }
            } else if (result === 1 || result === true) {
              this.router.navigate(['login'], {
                queryParams: { returnUrl: url },
              });
            } else {
              this.dialogRef = null;
            }
          });
        }
      }
    }
  }
}
